import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { LayoutController } from "ui-common";

import { styles } from "./base-image-cropper.css";

import "../modal/wow-modal";

export type ViewportType = "circle" | "square" | "cover" | "world" | "post";

async function supportsAvifEncoding(): Promise<boolean> {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, 1, 1);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/avif", 0.5));
    return blob !== null && blob.type === "image/avif";
  } catch {
    return false;
  }
}

const isAvifEncodingSupported = supportsAvifEncoding();

interface Transform {
  x: number;
  y: number;
  scale: number;
}

@customElement("base-image-cropper")
export class BaseImageCropper extends LitElement {
  static override styles = styles;

  // Minimum dimensions to prevent negative radius errors
  private static readonly MIN_CANVAS_WIDTH = 200;
  private static readonly MIN_CANVAS_HEIGHT = 200;
  private static readonly MIN_VIEWPORT_SIZE = 50;

  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  imageUrl?: string;

  @property({ type: String })
  viewport: ViewportType = "square";

  @property({ type: Number })
  outputWidth = 0;

  @property({ type: Number })
  outputHeight = 0;

  @property({ type: String })
  headerText = "Crop Image";

  @property({ type: String })
  footerButtonText = "Save";

  @state()
  private uploading = false;

  @state()
  private imageElement?: HTMLImageElement;

  @state()
  private viewportWidth: number = 300;

  @state()
  private viewportHeight: number = 300;

  @state()
  private viewportScale: number = 1;

  private aspectRatioDefault = {
    width: 1,
    height: 1,
  };
  private aspectRatioCover = {
    width: 27,
    height: 14,
  };
  private aspectRatioWorld = {
    width: 3,
    height: 1,
  };
  private aspectRatioPost = {
    width: 9,
    height: 16,
  };

  private transform: Transform = { x: 0, y: 0, scale: 1 };
  private canvasRef: HTMLCanvasElement = document.createElement("canvas");
  private qualityCanvas: HTMLCanvasElement = document.createElement("canvas");

  private layoutController = new LayoutController(this);

  constructor() {
    super();
    this.canvasRef.classList.add("crop-canvas");
    this.canvasRef.addEventListener("pointerdown", this.handlePointerDown.bind(this));
    this.canvasRef.addEventListener("pointermove", this.handlePointerMove.bind(this));
    this.canvasRef.addEventListener("pointerup", this.handlePointerUp.bind(this));
    this.canvasRef.addEventListener("wheel", this.handleWheel.bind(this));

    const resizeObserver = new ResizeObserver(() => {
      if (this.open && this.imageElement) {
        this.drawCanvas();
      }
    });
    resizeObserver.observe(this.canvasRef);

    this.qualityCanvas.style.display = "none";
  }

  private pointerState = {
    isPointerDown: false,
    lastPointerX: 0,
    lastPointerY: 0,
    initialDistance: undefined as number | undefined,
    initialScale: undefined as number | undefined,
  };
  private activePointers: PointerEvent[] = [];

  private loadImage = (url: string) => {
    const img = new Image();
    img.onload = () => {
      this.imageElement = img;
      this.requestUpdate();

      // Wait for layout to settle (modal + canvas sizing). Two frames is reliable.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Use the more robust reset which uses actual rendered viewport sizes.
          this.resetTransform();
          this.drawCanvas();
        });
      });
    };
    img.src = url;
  };

  /**
   * Compute the viewport size (in canvas pixels) the same way drawCanvas does,
   * but without drawing. This ensures resetTransform uses the same numbers as pointer handlers.
   */
  private computeRenderedViewport() {
    const config = this.getMaxDimensionsForViewport();
    const { width: canvasWidth, height: canvasHeight } = this.calculateCanvasDimensions();

    const maxWidth = canvasWidth - 20;
    const maxHeight = canvasHeight - 20;
    const scaleX = maxWidth / config.outputWidth;
    const scaleY = maxHeight / config.outputHeight;
    const scale = Math.max(0.1, Math.min(1, scaleX, scaleY));

    const viewportWidth = Math.max(config.outputWidth * scale, BaseImageCropper.MIN_VIEWPORT_SIZE);
    const viewportHeight = Math.max(config.outputHeight * scale, BaseImageCropper.MIN_VIEWPORT_SIZE);

    return {
      canvasWidth,
      canvasHeight,
      viewportWidth,
      viewportHeight,
      viewportScale: scale,
      config,
    };
  }

  private resetTransform = () => {
    if (!this.imageElement) {
      this.transform = { x: 0, y: 0, scale: 1 };
      return;
    }

    // Get image natural size
    const imageWidth = this.imageElement.naturalWidth;
    const imageHeight = this.imageElement.naturalHeight;

    // Compute the *rendered* viewport based on current canvas/modal sizes
    const { viewportWidth, viewportHeight } = this.computeRenderedViewport();

    // 1) fitScale ensures the whole image fits inside the viewport
    const fitScale = Math.min(viewportWidth / imageWidth, viewportHeight / imageHeight);

    // 2) fillScale ensures the viewport is completely covered by the image (no whitespace)
    const fillScale = Math.max(viewportWidth / imageWidth, viewportHeight / imageHeight);

    // Prefer fit (show whole image) but if that would leave whitespace, use fill.
    // Also clamp scales to reasonable bounds to avoid tiny or huge values.
    let initialScale = fitScale;

    // If fit would produce whitespace (i.e., image smaller than viewport along one axis),
    // switch to fill so the viewport is covered, preventing the white/empty area the user saw.
    if (initialScale * imageWidth < viewportWidth || initialScale * imageHeight < viewportHeight) {
      initialScale = fillScale;
    }

    // Optional: avoid extreme upscales — if you prefer not to up-scale images beyond native size,
    // you can clamp initialScale = Math.min(initialScale, 1);
    // For now we allow upscaling to avoid white space (matches pointer behavior expectations).
    initialScale = Math.max(0.01, Math.min(initialScale, 10));

    // Center image (x/y are offsets from canvas center)
    this.transform = {
      x: 0,
      y: 0,
      scale: initialScale,
    };

    // Ensure centered and within bounds
    this.constrainTransformToBounds();
  };

  private get aspectRatio() {
    switch (this.viewport) {
      case "circle":
      case "square":
      default:
        return this.aspectRatioDefault;
      case "cover":
        return this.aspectRatioCover;
      case "world":
        return this.aspectRatioWorld;
      case "post":
        return this.aspectRatioPost;
    }
  }

  // private get viewportConfig() {
  //   const aspectRatio = this.aspectRatio;

  //   const height = this.imageElement?.naturalHeight;
  //   const width = this.imageElement?.naturalWidth;

  //   if (!height || !width) {
  //     return {
  //       type: this.viewport,
  //       outputHeight: 300,
  //       outputWidth: 300,
  //     };
  //   }

  //   // Calculate the largest possible dimensions that maintain aspect ratio
  //   // while fitting inside the image
  //   const imageAspectRatio = width / height;
  //   const viewportAspectRatio = aspectRatio.width / aspectRatio.height;

  //   let outputWidth: number;
  //   let outputHeight: number;

  //   if (imageAspectRatio > viewportAspectRatio) {
  //     // Image is wider than desired ratio - constrain by height
  //     outputHeight = height;
  //     outputWidth = height * viewportAspectRatio;
  //   } else {
  //     // Image is taller than desired ratio - constrain by width
  //     outputWidth = width;
  //     outputHeight = width / viewportAspectRatio;
  //   }

  //   return {
  //     type: this.viewport,
  //     outputHeight: Math.floor(outputHeight),
  //     outputWidth: Math.floor(outputWidth),
  //   };
  // }

  private calculateCanvasDimensions() {
    // Try to get modal body element (canvas parent)
    const modalBody = this.renderRoot.querySelector("[slot=one-column]");
    if (!modalBody) {
      return {
        width: Math.max(this.outputWidth + 20, BaseImageCropper.MIN_CANVAS_WIDTH),
        height: Math.max(2 * this.outputHeight, BaseImageCropper.MIN_CANVAS_HEIGHT),
      };
    }
    const parentRect = modalBody.getBoundingClientRect();

    return {
      width: Math.max(parentRect.width, BaseImageCropper.MIN_CANVAS_WIDTH),
      height: Math.max(parentRect.height, BaseImageCropper.MIN_CANVAS_HEIGHT),
    };
  }

  private isCanvasDrawScheduled = false;

  /**
   * Safely draws an arc with radius validation to prevent negative radius errors
   */
  private safeArc(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    counterclockwise?: boolean,
  ) {
    const safeRadius = Math.max(0, radius);
    if (safeRadius > 0) {
      ctx.arc(x, y, safeRadius, startAngle, endAngle, counterclockwise);
    }
  }

  private drawCanvas = () => {
    if (this.isCanvasDrawScheduled) return;
    this.isCanvasDrawScheduled = true;

    const canvas = this.canvasRef;
    if (!canvas || !this.imageElement) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width: canvasWidth, height: canvasHeight } = this.calculateCanvasDimensions();
    this.canvasRef.style.width = `${canvasWidth}px`;
    this.canvasRef.style.height = `${canvasHeight}px`;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // handles scaling + reset
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.imageSmoothingEnabled = false;

    // Viewport config
    const config = this.getMaxDimensionsForViewport(); //this.viewportConfig;
    const maxWidth = canvasWidth - 20;
    const maxHeight = canvasHeight - 20;
    const scaleX = maxWidth / config.outputWidth;
    const scaleY = maxHeight / config.outputHeight;
    const scale = Math.max(0.1, Math.min(1, scaleX, scaleY));
    this.viewportScale = scale;
    this.viewportWidth = Math.max(config.outputWidth * scale, BaseImageCropper.MIN_VIEWPORT_SIZE);
    this.viewportHeight = Math.max(config.outputHeight * scale, BaseImageCropper.MIN_VIEWPORT_SIZE);
    const viewportWidth = this.viewportWidth;
    const viewportHeight = this.viewportHeight;
    const viewportX = (canvasWidth - viewportWidth) / 2;
    const viewportY = (canvasHeight - viewportHeight) / 2;

    requestAnimationFrame(() => {
      if (!this.imageElement) return;

      // Draw image with transform
      ctx.save();
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.translate(this.transform.x, this.transform.y);
      ctx.scale(this.transform.scale, this.transform.scale);
      const imgWidth = this.imageElement.naturalWidth;
      const imgHeight = this.imageElement.naturalHeight;
      ctx.drawImage(this.imageElement, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);
      ctx.restore();

      // Draw viewport overlay (excluding viewport area)
      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      ctx.beginPath();
      if (config.type === "circle") {
        ctx.rect(0, 0, canvasWidth, canvasHeight);
        this.safeArc(ctx, canvasWidth / 2, canvasHeight / 2, viewportWidth / 2, 0, 2 * Math.PI, true);
      } else {
        ctx.rect(0, 0, canvasWidth, canvasHeight);
        ctx.rect(viewportX, viewportY, viewportWidth, viewportHeight);
      }
      ctx.clip("evenodd");
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.restore();

      // Draw viewport border
      ctx.save();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;
      if (config.type === "circle") {
        ctx.beginPath();
        const centerX = Math.round(canvasWidth / 2) + 0.5;
        const centerY = Math.round(canvasHeight / 2) + 0.5;
        const radius = Math.round(viewportWidth / 2);
        this.safeArc(ctx, centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        const x = Math.round(viewportX) + 0.5;
        const y = Math.round(viewportY) + 0.5;
        const width = Math.round(viewportWidth);
        const height = Math.round(viewportHeight);
        ctx.strokeRect(x, y, width, height);
      }
      ctx.restore();
      this.isCanvasDrawScheduled = false;
    });
  };

  private handlePointerDown = (event: PointerEvent) => {
    event.preventDefault();
    this.activePointers.push(event);
    if (this.activePointers.length === 1) {
      this.pointerState.isPointerDown = true;
      this.pointerState.lastPointerX = event.clientX;
      this.pointerState.lastPointerY = event.clientY;
    } else if (this.activePointers.length === 2) {
      const [p1, p2] = this.activePointers;
      if (p1 && p2) {
        const initialDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
        // Only set initial values if we have a reasonable distance to prevent division by small numbers
        if (initialDistance > 20) {
          this.pointerState.initialDistance = initialDistance;
          this.pointerState.initialScale = this.transform.scale;
        }
      }
    }
  };

  private handlePointerMove = (event: PointerEvent) => {
    event.preventDefault();

    if (!this.imageElement) return;
    const imgWidth = this.imageElement.naturalWidth;
    const imgHeight = this.imageElement.naturalHeight;

    const index = this.activePointers.findIndex((p) => p.pointerId === event.pointerId);
    if (index !== -1) {
      this.activePointers[index] = event;
    }

    if (this.activePointers.length === 1 && this.pointerState.isPointerDown) {
      const deltaX = event.clientX - this.pointerState.lastPointerX;
      const deltaY = event.clientY - this.pointerState.lastPointerY;

      const directionX = deltaX >= 0 ? 1 : -1;
      const directionY = deltaY >= 0 ? 1 : -1;

      const barrierX = (this.transform.scale * imgWidth - this.viewportWidth) * 0.5 * directionX;
      const barrierY = (this.transform.scale * imgHeight - this.viewportHeight) * 0.5 * directionY;

      if (
        (directionX === 1 && barrierX > this.transform.x + deltaX) ||
        (directionX === -1 && barrierX < this.transform.x + deltaX)
      ) {
        this.transform.x += deltaX;
        this.pointerState.lastPointerX = event.clientX;
      } else {
        this.transform.x = barrierX - 0.5 * directionX;
        this.pointerState.lastPointerX = event.clientX;
      }

      if (
        (directionY === 1 && barrierY > this.transform.y + deltaY) ||
        (directionY === -1 && barrierY < this.transform.y + deltaY)
      ) {
        this.transform.y += deltaY;
        this.pointerState.lastPointerY = event.clientY;
      } else {
        this.transform.y = barrierY - 0.5 * directionY;
        this.pointerState.lastPointerX = event.clientX;
      }

      this.drawCanvas();
    } else if (this.activePointers.length === 2) {
      const [p1, p2] = this.activePointers;
      if (p1 && p2 && this.pointerState.initialDistance && this.pointerState.initialScale) {
        const currentDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);

        // Validate distance measurements to prevent invalid calculations
        if (currentDistance > 10 && this.pointerState.initialDistance > 10) {
          const distanceRatio = currentDistance / this.pointerState.initialDistance;
          const targetScale = this.pointerState.initialScale * distanceRatio;

          // Apply scale directly for smooth, responsive zooming
          this.zoomToViewportCenter(targetScale, true);
        }
      }
    }
  };

  private handlePointerUp = (event: PointerEvent) => {
    this.activePointers = this.activePointers.filter((p) => p.pointerId !== event.pointerId);
    if (this.activePointers.length === 0) {
      this.pointerState.isPointerDown = false;
      this.pointerState.initialDistance = undefined;
      this.pointerState.initialScale = undefined;
    } else if (this.activePointers.length === 1) {
      const remaining = this.activePointers[0];
      if (remaining) {
        this.pointerState.lastPointerX = remaining.clientX;
        this.pointerState.lastPointerY = remaining.clientY;
        this.pointerState.initialDistance = undefined;
        this.pointerState.initialScale = undefined;
      }
    }
  };

  private handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    const scaleChange = event.deltaY > 0 ? 0.9 : 1.1;
    this.zoomToViewportCenter(scaleChange);
  };

  /**
   * Zooms to the center of the viewport, maintaining the same image area in focus
   */
  private zoomToViewportCenter(scaleChange: number, absolute: boolean = false) {
    if (!this.imageElement) return;

    const oldScale = this.transform.scale;
    let newScale = absolute
      ? Math.max(0.05, Math.min(5, scaleChange))
      : Math.max(0.05, Math.min(5, oldScale * scaleChange));

    const imgWidth = this.imageElement.naturalWidth;
    const imgHeight = this.imageElement.naturalHeight;

    // Ensure minimum scale constraints
    if (newScale * imgWidth < this.viewportWidth) {
      newScale = this.viewportWidth / imgWidth;
    }
    if (newScale * imgHeight < this.viewportHeight) {
      newScale = this.viewportHeight / imgHeight;
    }

    // If scale didn't actually change, don't adjust position
    if (newScale === oldScale) return;

    // Calculate the current canvas dimensions to know viewport center
    const { width: canvasWidth, height: canvasHeight } = this.calculateCanvasDimensions();
    const viewportCenterX = canvasWidth / 2;
    const viewportCenterY = canvasHeight / 2;

    // Calculate what point in the image is currently at the viewport center
    // First, find where the viewport center is relative to the image center in canvas space
    const offsetFromImageCenterX = viewportCenterX - (canvasWidth / 2 + this.transform.x);
    const offsetFromImageCenterY = viewportCenterY - (canvasHeight / 2 + this.transform.y);

    // Convert this offset to image coordinates at the old scale
    const imagePointX = offsetFromImageCenterX / oldScale;
    const imagePointY = offsetFromImageCenterY / oldScale;

    // Now calculate where this same image point should be at the new scale
    const newOffsetX = imagePointX * newScale;
    const newOffsetY = imagePointY * newScale;

    // Adjust transform to keep the same image point at viewport center
    const newTransformX = this.transform.x + (offsetFromImageCenterX - newOffsetX);
    const newTransformY = this.transform.y + (offsetFromImageCenterY - newOffsetY);

    // Apply the new scale and adjusted position
    this.transform.scale = newScale;
    this.transform.x = newTransformX;
    this.transform.y = newTransformY;

    // Ensure the new position is within bounds
    this.constrainTransformToBounds();
    this.drawCanvas();
  }

  /**
   * Ensures the transform position stays within valid bounds
   */
  private constrainTransformToBounds() {
    if (!this.imageElement) return;

    const imgWidth = this.imageElement.naturalWidth;
    const imgHeight = this.imageElement.naturalHeight;

    const maxOffsetX = Math.max(0, (this.transform.scale * imgWidth - this.viewportWidth) * 0.5);
    const maxOffsetY = Math.max(0, (this.transform.scale * imgHeight - this.viewportHeight) * 0.5);

    this.transform.x = Math.max(-maxOffsetX, Math.min(maxOffsetX, this.transform.x));
    this.transform.y = Math.max(-maxOffsetY, Math.min(maxOffsetY, this.transform.y));
  }

  private handleCancel = (e: Event) => {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    this.dispatchEvent(new CustomEvent("cropper-cancel"));
  };

  private handleSave = async () => {
    this.uploading = true;
    const file = await this.getCroppedFile();
    this.uploading = false;
    this.dispatchEvent(
      new CustomEvent("cropper-save", {
        detail: { file },
      }),
    );
  };

  private async getCroppedFile(): Promise<File | null> {
    if (!this.imageElement) return null;
    const config = this.getMaxDimensionsForViewport(); //this.viewportConfig;

    const imageWidth = this.imageElement.naturalWidth;
    const imageHeight = this.imageElement.naturalHeight;

    const qualityCanvas = this.qualityCanvas;
    const qualityCtx = qualityCanvas.getContext("2d");
    if (!qualityCtx) return null;

    qualityCanvas.width = config.outputWidth;
    qualityCanvas.height = config.outputHeight;

    // Enable high-quality image smoothing
    qualityCtx.imageSmoothingEnabled = true;
    qualityCtx.imageSmoothingQuality = "high";

    // Apply clipping for circle viewport
    if (config.type === "circle") {
      qualityCtx.beginPath();
      this.safeArc(
        qualityCtx,
        qualityCanvas.width / 2,
        qualityCanvas.height / 2,
        qualityCanvas.width / 2,
        0,
        2 * Math.PI,
      );
      qualityCtx.clip();
    }

    // Get current canvas dimensions
    const { width: canvasWidth, height: canvasHeight } = this.calculateCanvasDimensions();

    // Calculate viewport position and size in canvas coordinates
    const viewportX = (canvasWidth - this.viewportWidth) / 2;
    const viewportY = (canvasHeight - this.viewportHeight) / 2;

    // Calculate the image's position and size when drawn on canvas
    const imageCanvasWidth = imageWidth * this.transform.scale;
    const imageCanvasHeight = imageHeight * this.transform.scale;

    // Calculate where the top-left corner of the image is in canvas coordinates
    const imageCanvasX = canvasWidth / 2 + this.transform.x - imageCanvasWidth / 2;
    const imageCanvasY = canvasHeight / 2 + this.transform.y - imageCanvasHeight / 2;

    // Calculate what part of the image is visible in the viewport
    // Convert viewport coordinates to image coordinates
    const viewportLeftInImage = (viewportX - imageCanvasX) / this.transform.scale;
    const viewportTopInImage = (viewportY - imageCanvasY) / this.transform.scale;
    const viewportWidthInImage = this.viewportWidth / this.transform.scale;
    const viewportHeightInImage = this.viewportHeight / this.transform.scale;

    // Clamp to image boundaries
    const sourceX = Math.max(0, Math.min(viewportLeftInImage, imageWidth));
    const sourceY = Math.max(0, Math.min(viewportTopInImage, imageHeight));
    const sourceRight = Math.max(0, Math.min(viewportLeftInImage + viewportWidthInImage, imageWidth));
    const sourceBottom = Math.max(0, Math.min(viewportTopInImage + viewportHeightInImage, imageHeight));

    const sourceWidth = sourceRight - sourceX;
    const sourceHeight = sourceBottom - sourceY;

    // Ensure we have valid dimensions
    if (sourceWidth <= 0 || sourceHeight <= 0) {
      // Fallback: use center crop
      const size = Math.min(imageWidth, imageHeight);
      const centerX = imageWidth / 2;
      const centerY = imageHeight / 2;
      qualityCtx.drawImage(
        this.imageElement,
        centerX - size / 2,
        centerY - size / 2,
        size,
        size,
        0,
        0,
        qualityCanvas.width,
        qualityCanvas.height,
      );
    } else {
      // Draw the exact portion that's visible in the viewport
      qualityCtx.drawImage(
        this.imageElement,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        qualityCanvas.width,
        qualityCanvas.height,
      );
    }

    let endCanvas = qualityCanvas;

    // Fallback required for file-cropper component
    if (this.viewport === "circle" || this.viewport === "square") {
      const fixedCanvas = document.createElement("canvas");
      const fixedCtx = fixedCanvas.getContext("2d");

      fixedCanvas.width = 300;
      fixedCanvas.height = 300;

      if (fixedCtx) {
        fixedCtx.drawImage(qualityCanvas, 0, 0, fixedCanvas.width, fixedCanvas.height);
        endCanvas = fixedCanvas;
      }
    }

    const avifSupported = await isAvifEncodingSupported;
    let type = avifSupported ? "image/avif" : "image/webp";
    let extension = avifSupported ? "avif" : "webp";

    const createBlob = (mimeType: string, quality: number): Promise<Blob | null> =>
      new Promise((resolve) => endCanvas.toBlob((blob) => resolve(blob), mimeType, quality));

    let blob = await createBlob(type, 1);

    // Verify the blob was actually encoded in the requested format.
    // Some browsers (e.g. iOS Safari) silently fall back to PNG when they
    // can't encode the requested format, producing oversized blobs where
    // the quality parameter is ignored.
    if (blob && blob.type !== type) {
      type = "image/webp";
      extension = "webp";
      blob = await createBlob(type, 1);
    }

    // Cover images (live event thumbnails) must stay ≤600KB for WhatsApp/OG preview
    if (this.viewport === "cover" && blob && blob.size > 600 * 1024) {
      for (const q of [0.85, 0.7, 0.55, 0.4]) {
        blob = await createBlob(type, q);
        if (blob && blob.size <= 600 * 1024) break;
      }

      // iOS Safari ignores the quality parameter for WebP/AVIF canvas encoding.
      // Fall back to JPEG where quality reduction is universally supported.
      if (blob && blob.size > 600 * 1024) {
        for (const q of [0.85, 0.7, 0.55, 0.4]) {
          blob = await createBlob("image/jpeg", q);
          if (blob && blob.size <= 600 * 1024) break;
        }
        if (blob && blob.type === "image/jpeg") {
          type = "image/jpeg";
          extension = "jpg";
        }
      }
    }

    if (!blob) return null;
    return new File([blob], "cropped-image." + extension, { type });
  }

  private getMaxDimensionsForViewport() {
    switch (this.viewport) {
      case "circle":
      case "square":
        return { outputWidth: 512, outputHeight: 512, type: this.viewport }; // Good for avatars
      case "cover":
        return { outputWidth: 1200, outputHeight: 620, type: this.viewport };
      case "world":
        return { outputWidth: 1200, outputHeight: 400, type: this.viewport };
      case "post":
        return { outputWidth: 1080, outputHeight: 1920, type: this.viewport }; // Portrait posts
      default:
        return { outputWidth: 1200, outputHeight: 620, type: this.viewport };
    }
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("imageUrl") && this.imageUrl) {
      this.loadImage(this.imageUrl);
    }
  }

  protected override render() {
    if (!this.open) return "";

    if (this.viewport === "square" || this.viewport === "circle") {
      this.canvasRef.style.aspectRatio = "1";
    }

    return html`
      <wow-modal
        mobile-fullscreen
        @close=${this.handleCancel}
        @footer-button-clicked=${this.handleSave}
        variant="responsive"
      >
        <span slot="header">${this.headerText}</span>
        <div slot="one-column">${this.canvasRef} ${this.qualityCanvas}</div>
        <span slot="footer-button-text">${this.uploading ? "Uploading..." : this.footerButtonText}</span>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "base-image-cropper": BaseImageCropper;
  }
}
